import cn from 'classnames'
import {FC, ReactElement, ReactNode} from 'react'

import {Link, Trans} from '@/i18n'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

import {Badge, BadgeColor} from '../badges/Badge'
import {Button} from '../content/Button'
import {ButtonVariant} from '../content/ButtonTypes'
import {ListItemWithCheckmark} from '../content/ListItemWithCheckmark'
import {Text, TextVariant} from '../content/Text'
import {ArrowIcon} from '../icons/ArrowIcon'
import {CheckCircleOutlineIcon} from '../icons/CheckCircleOutlineIcon'
import {CardWrapper} from './CardWrapper'

type ButtonProps = {text: string; href: string; onClick?: () => void}

type PricingOfferCardProps = {
  icon: ReactNode
  title: string
  learnMore?: false | ButtonProps
  badge: {text: string; color: BadgeColor; icon?: ReactNode}
  price: ReactNode
  bullets?: (string | ReactElement<typeof Trans>)[]
  button: ButtonProps
  isHighlighted?: boolean
}

export const PricingOfferCard: FC<PricingOfferCardProps> = ({
  icon,
  title,
  learnMore,
  badge,
  price,
  bullets,
  button,
  isHighlighted = false,
}) => (
  <>
    <CardWrapper hasBorder={false} isHoverable={false}>
      <Link href={button.href} onClick={button.onClick}>
        <div className={cn('card', isHighlighted && 'highlighted')}>
          <div>
            <div className="header">
              <div className="icon">{icon}</div>
              <Text variant={TextVariant.h4} element="h3" align="center">
                {title}
              </Text>
              {learnMore !== undefined &&
                (learnMore ? (
                  <Link
                    href={learnMore.href}
                    onClick={(e) => {
                      e.stopPropagation()
                      learnMore.onClick?.()
                    }}
                  >
                    <Button type="button" icon={<ArrowIcon />} isHoverable>
                      {learnMore.text}
                    </Button>
                  </Link>
                ) : (
                  <div className="learn-more-placeholder" />
                ))}
            </div>
            <div className="divider" />
            <div className="meta">
              <Badge title={badge.text} color={badge.color} icon={badge.icon} />
              <div className="price">
                <Text variant={TextVariant.h5} element="p" align="center">
                  {price}
                </Text>
              </div>
            </div>
            {bullets && bullets.length > 0 && (
              <ul className="bullet-points divider">
                {bullets.map((text, i) => (
                  <ListItemWithCheckmark
                    key={i}
                    text={text}
                    icon={<CheckCircleOutlineIcon color={theme.palette.success.main} />}
                    gap={theme.spacing[2.5]}
                  />
                ))}
              </ul>
            )}
          </div>
          <div className="button-wrapper divider">
            <Button
              type="button"
              variant={isHighlighted ? ButtonVariant.primary : ButtonVariant.secondary}
              fullWidth
            >
              {button.text}
            </Button>
          </div>
        </div>
      </Link>
    </CardWrapper>

    <style jsx>{`
      .card {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        height: 100%;
        padding: ${theme.spacing[4]};
        background-color: ${theme.palette.gray[50]};
        box-shadow: inset 0 0 0 1px ${theme.palette.gray[200]};
        border-radius: 16px;
      }
      .highlighted {
        background-color: ${theme.palette.background.butter};
        box-shadow: 0 15px 15px 0 rgba(241, 228, 221, 0.5),
          inset 0 0 0 1px ${theme.palette.butter[400]};
      }
      .highlighted .icon,
      .highlighted .divider {
        background-color: ${theme.palette.background.butter};
        border-color: ${theme.palette.butter[400]};
      }
      .learn-more-placeholder {
        height: 24px;
      }
      .header {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: ${theme.spacing[1.5]};
        margin-bottom: ${theme.spacing[3.5]};
      }
      .icon {
        display: flex;
        padding: ${theme.spacing[2]};
        background-color: ${theme.palette.gray[50]};
        border: 1px solid ${theme.palette.gray[200]};
        border-radius: 50%;
        margin-top: -52px;
        margin-bottom: ${theme.spacing[3]};
      }
      .meta {
        text-align: center;
        width: 100%;
        margin-top: ${theme.spacing[3.5]};
      }
      .bullet-points {
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing[2.5]};
        padding-top: ${theme.spacing[5]};
        margin-bottom: ${theme.spacing[5]};
      }
      .button-wrapper {
        padding-top: ${theme.spacing[5]};
      }
      .divider {
        width: 100%;
        border-top: 1px solid ${theme.palette.gray[200]};
      }
      .price {
        margin-top: ${theme.spacing[1]};
        margin-bottom: ${theme.spacing[2.5]};
      }
      @media ${MOBILE_SCREENSIZE} {
        .card {
          margin-top: ${theme.spacing[3]};
        }
        .icon {
          margin-top: -${theme.spacing[5.5]};
          margin-bottom: ${theme.spacing[2]};
        }
        .bullet-points {
          padding-top: ${theme.spacing[4]};
          margin-bottom: ${theme.spacing[4]};
        }
        .button-wrapper {
          padding-top: ${theme.spacing[4]};
        }
      }
    `}</style>
  </>
)
